import { useState, useEffect } from 'react';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';
import { API } from '../lib/api';

const OVERLOAD_RULE_PRESENTATION = [
  {
    title: "Objectif et Score",
    points: [
      "Score limite: 8 points pour gagner la partie.",
      "Temps par manche: 5 minutes.",
      "L'appareil (Device): objet central a recuperer. S'il sort des limites ou est perdu, il est reinitialise apres 15 secondes.",
      "AFL time declenche au bout de 4 points."
    ]
  },
  {
    title: "Mecaniques du Porteur (Carrier)",
    points: [
      "Visibilite: porteur marque par VFX et outlines.",
      "Radar: le porteur n'a pas de radar personnel.",
      "Effet EMP: capturer une zone declenche un EMP de 10 secondes qui brouille fortement le HUD adverse."
    ]
  },
  {
    title: "Vie et Mobilite",
    points: [
      "Vies: illimitees.",
      "Sante: 100 HP (normal).",
      "Reapparition: 3,5 secondes de delai.",
      "Sprint tactique: active."
    ]
  },
  {
    title: "Combat et Tactique",
    points: [
      "Friendly Fire: active.",
      "Killcam: desactivee.",
      "Sudden Death: pas de mort subite par defaut, Time to Beat en cas d'egalite."
    ]
  }
];

function CDLInfo({ onClose, isPublic = false }) {
  const [weapons, setWeapons] = useState(null);
  const [rules, setRules] = useState(null);
  const [maps, setMaps] = useState([]);
  const [activeTab, setActiveTab] = useState('weapons');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCDLData();
  }, []);

  const loadCDLData = async () => {
    setLoading(true);
    try {
      const baseUrl = isPublic ? `${API}/public` : API;
      const [weaponsRes, rulesRes, mapsRes] = await Promise.all([
        axios.get(`${baseUrl}/cdl/weapons`),
        axios.get(`${baseUrl}/cdl/rules`),
        axios.get(`${baseUrl}/maps`)
      ]);
      setWeapons(weaponsRes.data);
      setRules(rulesRes.data);
      setMaps(mapsRes.data.maps);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 fade-in">
        <div className="bg-[#0a0a0a] border border-[#262626] p-8 max-w-4xl w-full corner-decoration relative">
          <LoadingSpinner text="Chargement des informations CDL..." />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 fade-in overflow-y-auto">
      <div className="bg-[#0a0a0a] border border-[#262626] p-8 max-w-4xl w-full corner-decoration relative slide-in my-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-primary font-black text-2xl uppercase">GUIDE CDL BLACK OPS 7</h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-white font-secondary text-xs uppercase"
          >
            FERMER
          </button>
        </div>

        <div className="flex gap-4 mb-6 border-b border-white/10 pb-4">
          <button
            onClick={() => setActiveTab('weapons')}
            className={`font-secondary text-xs uppercase px-4 py-2 transition-colors ${
              activeTab === 'weapons' 
                ? 'text-[#ff4500] border-b-2 border-[#ff4500]' 
                : 'text-muted-foreground hover:text-white'
            }`}
          >
            ARMES
          </button>
          <button
            onClick={() => setActiveTab('maps')}
            className={`font-secondary text-xs uppercase px-4 py-2 transition-colors ${
              activeTab === 'maps' 
                ? 'text-[#ff4500] border-b-2 border-[#ff4500]' 
                : 'text-muted-foreground hover:text-white'
            }`}
          >
            CARTES
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto pr-4">
          {activeTab === 'weapons' && weapons && (
            <div className="space-y-6">
              <h4 className="font-primary font-bold text-lg uppercase text-[#00ff9d]">Fusils d'Assaut</h4>
              {weapons.assault_rifles.map((weapon, idx) => (
                <div key={idx} className="bg-[#1a1a1a] border border-[#262626] p-6 hover:border-[#ff4500]/50 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h5 className="font-primary font-bold text-xl uppercase">{weapon.name}</h5>
                      <p className="font-secondary text-xs text-[#ff4500] uppercase mt-1">{weapon.weapon_class || weapon.class}</p>
                    </div>
                    {weapon.image_url && (
                      <img
                        src={weapon.image_url}
                        alt={weapon.name}
                        className="w-50 h-20 object-contain border border-white/10 bg-[#0a0a0a] p-1"
                      />
                    )}
                  </div>
                  <p className="font-secondary text-sm text-muted-foreground mb-4">{weapon.description}</p>
                  <div>
                    <p className="font-secondary text-xs text-muted-foreground uppercase mb-2">Accessoires recommandés:</p>
                    <div className="flex flex-wrap gap-2">
                      {(weapon.attachments || []).map((att, i) => (
                        <span key={i} className="font-secondary text-xs bg-[#ff4500]/20 text-[#ff4500] px-3 py-1 border border-[#ff4500]/50">
                          {att}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              <h4 className="font-primary font-bold text-lg uppercase text-[#00ff9d] mt-8">Mitraillettes</h4>
              {weapons.smgs.map((weapon, idx) => (
                <div key={idx} className="bg-[#1a1a1a] border border-[#262626] p-6 hover:border-[#ff4500]/50 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h5 className="font-primary font-bold text-xl uppercase">{weapon.name}</h5>
                      <p className="font-secondary text-xs text-[#ff4500] uppercase mt-1">{weapon.weapon_class || weapon.class}</p>
                    </div>
                    {weapon.image_url && (
                      <img
                        src={weapon.image_url}
                        alt={weapon.name}
                        className="w-50 h-20 object-contain border border-white/10 bg-[#0a0a0a] p-1"
                      />
                    )}
                  </div>
                  <p className="font-secondary text-sm text-muted-foreground mb-4">{weapon.description}</p>
                  <div>
                    <p className="font-secondary text-xs text-muted-foreground uppercase mb-2">Accessoires recommandés:</p>
                    <div className="flex flex-wrap gap-2">
                      {(weapon.attachments || []).map((att, i) => (
                        <span key={i} className="font-secondary text-xs bg-[#ff4500]/20 text-[#ff4500] px-3 py-1 border border-[#ff4500]/50">
                          {att}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              <h4 className="font-primary font-bold text-lg uppercase text-[#00ff9d] mt-8">Fusils de Précision</h4>
              {weapons.snipers.map((weapon, idx) => (
                <div key={idx} className="bg-[#1a1a1a] border border-[#262626] p-6 hover:border-[#ff4500]/50 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h5 className="font-primary font-bold text-xl uppercase">{weapon.name}</h5>
                      <p className="font-secondary text-xs text-[#ff4500] uppercase mt-1">{weapon.weapon_class || weapon.class}</p>
                    </div>
                    {weapon.image_url && (
                      <img
                        src={weapon.image_url}
                        alt={weapon.name}
                        className="w-45 h-20 object-contain border border-white/10 bg-[#0a0a0a] p-1"
                      />
                    )}
                  </div>
                  <p className="font-secondary text-sm text-muted-foreground mb-4">{weapon.description}</p>
                  <div>
                    <p className="font-secondary text-xs text-muted-foreground uppercase mb-2">Accessoires recommandés:</p>
                    <div className="flex flex-wrap gap-2">
                      {(weapon.attachments || []).map((att, i) => (
                        <span key={i} className="font-secondary text-xs bg-[#ff4500]/20 text-[#ff4500] px-3 py-1 border border-[#ff4500]/50">
                          {att}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'maps' && (
            <div className="space-y-6">
              {maps.map((map, idx) => (
                <div key={idx} className="bg-[#1a1a1a] border border-[#262626] overflow-hidden hover:border-[#ff4500]/50 transition-colors">
                  <img src={map.image} alt={map.name} className="w-full max-h-[400px] object-contain mb-4" />

                  <div className="p-6">
                    <h5 className="font-primary font-bold text-xl uppercase mb-2">{map.name}</h5>
                    <p className="font-secondary text-sm text-muted-foreground mb-4">{map.description}</p>
                    <div>
                      <p className="font-secondary text-xs text-muted-foreground uppercase mb-2">Modes disponibles:</p>
                      <div className="flex flex-wrap gap-2">
                        {map.modes.map((mode, i) => (
                          <span key={i} className="font-secondary text-xs bg-[#00ff9d]/20 text-[#00ff9d] px-3 py-1 border border-[#00ff9d]/50">
                            {mode}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CDLInfo;
